/**
 * expiryUtils.ts
 *
 * Pure, side-effect-free utility functions for expiry-item business logic.
 * Nothing here imports React or touches state — functions can be called from
 * hooks, components or tests alike.
 */

import { differenceInDays, parseISO, format, startOfDay } from "date-fns";
import type { ExpiryItem } from "../hooks/useExpiryItems";
import type { ItemStatus, CardFilter, SummaryCardData } from "../types";
import {
  HIGH_RISK_DAYS,
  UPCOMING_DAYS,
  REMINDER_THRESHOLDS,
  DATE_DISPLAY_FORMAT,
  DATE_LOCALE,
  DATE_LOCALE_OPTIONS,
  COLOR,
  CSV_HEADERS,
  CSV_MIME,
} from "../constants/expiry";

// ─── Status ───────────────────────────────────────────────────────────────────

export interface StatusResult {
  status: ItemStatus;
  label: string;
}

/**
 * Derives the display status and human-readable label for an expiry item
 * based solely on its expiry_date relative to today.
 */
export function getItemStatus(item: ExpiryItem): StatusResult {
  if (!item.expiry_date) {
    return { status: "missing", label: "Missing expiry" };
  }

  // Compare both dates at midnight so "1 day left" shows correctly throughout the day
  const today = startOfDay(new Date());
  const expiryDate = startOfDay(parseISO(item.expiry_date));
  const days = differenceInDays(expiryDate, today);

  if (days < 0) return { status: "expired", label: "Expired" };
  if (days === 0) return { status: "high-risk", label: "Expires today" };
  if (days <= HIGH_RISK_DAYS)
    return {
      status: "high-risk",
      label: `${days} day${days === 1 ? "" : "s"} left`,
    };
  if (days <= UPCOMING_DAYS)
    return { status: "upcoming", label: `In ${days} days` };

  return { status: "active", label: "Active" };
}

// ─── Date colour ──────────────────────────────────────────────────────────────

/**
 * Returns the Tailwind text-colour class for an expiry date cell.
 * Mirrors the same thresholds used by `getItemStatus`.
 */
export function getDateColorClass(item: ExpiryItem): string {
  if (!item.expiry_date) return COLOR.muted;

  const today = startOfDay(new Date());
  const expiryDate = startOfDay(parseISO(item.expiry_date));
  const days = differenceInDays(expiryDate, today);

  if (days <= HIGH_RISK_DAYS) return COLOR.danger;
  if (days <= UPCOMING_DAYS) return COLOR.warning;

  return COLOR.safe;
}

// ─── Reminder ─────────────────────────────────────────────────────────────────

/**
 * Returns the number of days from today until the *next* reminder fires,
 * or `null` if the item is already expired / all reminders have been sent.
 *
 * @param thresholds - Days-before-expiry values to check (e.g. [30, 7, 1]).
 *   Falls back to the built-in REMINDER_THRESHOLDS constant when not supplied.
 */
export function getNextReminderDays(
  item: ExpiryItem,
  thresholds: readonly number[] = REMINDER_THRESHOLDS
): number | null {
  if (!item.expiry_date) return null;

  const today = startOfDay(new Date());
  const expiryDate = startOfDay(parseISO(item.expiry_date));
  const daysToExpiry = differenceInDays(expiryDate, today);

  if (daysToExpiry < 0) return null;

  // For each threshold (days-before-expiry), compute how many days from TODAY
  // until that reminder fires.  Filter out already-fired ones (negative).
  const next = [...thresholds]
    .map((t) => daysToExpiry - t)
    .filter((d) => d >= 0)
    .sort((a, b) => a - b)[0];

  return next ?? null;
}

// ─── Date formatting ──────────────────────────────────────────────────────────

/** Formats an ISO date string for display in table cells (e.g. "Dec 31, 2025"). */
export function formatDisplayDate(dateStr: string): string {
  return format(parseISO(dateStr), DATE_DISPLAY_FORMAT);
}

/**
 * Formats a bare YYYY-MM-DD string into a full human-readable date
 * for use in toast messages (e.g. "December 31, 2025").
 */
export function formatLocalDate(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString(
    DATE_LOCALE,
    DATE_LOCALE_OPTIONS
  );
}

// ─── Summary card data ────────────────────────────────────────────────────────

/**
 * Derives the three summary card datasets (High Risk, Upcoming, Needs Attention)
 * from a list of expiry items.
 *
 * Each card also carries a `filter` value so clicking it can filter the table.
 */
export function buildSummaryCards(items: ExpiryItem[]): SummaryCardData[] {
  const today = startOfDay(new Date());

  const highRisk = items.filter((item) => {
    if (!item.expiry_date) return false;
    const days = differenceInDays(
      startOfDay(parseISO(item.expiry_date)),
      today
    );
    return days >= 0 && days <= HIGH_RISK_DAYS;
  }).length;

  const upcoming = items.filter((item) => {
    if (!item.expiry_date) return false;
    const days = differenceInDays(
      startOfDay(parseISO(item.expiry_date)),
      today
    );
    return days > HIGH_RISK_DAYS && days <= UPCOMING_DAYS;
  }).length;

  const needsAttention = items.filter((item) => !item.expiry_date).length;

  return [
    {
      title: "High Risk",
      count: highRisk,
      description: `Expires within ${HIGH_RISK_DAYS} days`,
      variant: "high-risk",
      filter: "high-risk",
    },
    {
      title: "Upcoming",
      count: upcoming,
      description: `Expires within ${UPCOMING_DAYS} days`,
      variant: "upcoming",
      filter: "upcoming",
    },
    {
      title: "Needs Attention",
      count: needsAttention,
      description: "Missing expiry dates",
      variant: "needs-attention",
      filter: "needs-attention",
    },
  ];
}

// ─── List filters ─────────────────────────────────────────────────────────────

/** Filters items by free-text search across name and category. */
export function filterBySearch(
  items: ExpiryItem[],
  query: string
): ExpiryItem[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase();
  return items.filter(
    (item) =>
      item.name.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q)
  );
}

/** Filters items by the active card filter (high-risk / upcoming / needs-attention). */
export function filterByCard(
  items: ExpiryItem[],
  filter: CardFilter
): ExpiryItem[] {
  if (!filter) return items;

  const today = startOfDay(new Date());
  return items.filter((item) => {
    if (filter === "needs-attention") return !item.expiry_date;
    if (!item.expiry_date) return false;

    const days = differenceInDays(
      startOfDay(parseISO(item.expiry_date)),
      today
    );
    if (filter === "high-risk") return days >= 0 && days <= HIGH_RISK_DAYS;
    if (filter === "upcoming")
      return days > HIGH_RISK_DAYS && days <= UPCOMING_DAYS;

    return false;
  });
}

// ─── CSV export helper ────────────────────────────────────────────────────────

/** Converts a list of items to a CSV Blob ready for download. */
export function buildCsv(items: ExpiryItem[]): Blob {
  const rows = [
    CSV_HEADERS.join(","),
    ...items.map((item) =>
      [
        `"${item.name}"`,
        `"${item.category}"`,
        item.expiry_date ?? "",
        getItemStatus(item).label,
      ].join(",")
    ),
  ].join("\n");

  return new Blob([rows], { type: CSV_MIME });
}
